import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { CheckCircle, XCircle, Clock, DollarSign, User, AlertTriangle, Shield, Crown, RefreshCw, Wallet, Calendar, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayoutApprovalDashboard = ({ isOwner = false }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/payout-approvals/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/payout-approvals/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingRequests(pendingData.pending_requests || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch payout data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      const res = await fetch(`${API_URL}/api/payout-approvals/approve/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Payout approved! $${data.payout?.amount?.toFixed(2) || 'Amount'} sent.`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve payout');
      }
    } catch (error) {
      toast.error('Failed to approve payout');
    } finally {
      setProcessing(null);
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(selectedRequest.request_id);
    try {
      const res = await fetch(`${API_URL}/api/payout-approvals/reject/${selectedRequest.request_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectionReason || 'No reason provided' })
      });

      if (res.ok) {
        toast.success('Payout request rejected');
        setRejectDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reject payout');
      }
    } catch (error) {
      toast.error('Failed to reject payout');
    } finally {
      setProcessing(null);
    }
  };

  const getTypeIcon = (request) => {
    if (request.is_admin) {
      return <Shield className="h-4 w-4 text-purple-600" />;
    }
    return <User className="h-4 w-4 text-blue-600" />;
  };

  const getTypeBadge = (request) => {
    if (request.is_admin) {
      return (
        <Badge className="bg-purple-100 text-purple-800 border-purple-200">
          <Shield className="h-3 w-3 mr-1" />
          Admin
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-100 text-blue-800 border-blue-200">
        <User className="h-3 w-3 mr-1" />
        Provider
      </Badge>
    );
  };

  const getFrequencyBadge = (frequency) => {
    if (frequency === 'weekly') {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Weekly</Badge>;
    }
    return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Daily</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payout-approval-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-green-600" />
            Payout Approvals
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {isOwner ? 'Review all payout requests including admin payouts' : 'Review provider payout requests'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-payouts-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-700 font-medium">Pending</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.pending_count}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-700 font-medium">Approved</p>
                  <p className="text-2xl font-bold text-green-900">{stats.approved_count}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-700 font-medium">Rejected</p>
                  <p className="text-2xl font-bold text-red-900">{stats.rejected_count}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-700 font-medium">Pending Amount</p>
                  <p className="text-2xl font-bold text-blue-900">${stats.total_pending_amount?.toFixed(2) || '0.00'}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner for Owners */}
      {isOwner && (
        <Card className="bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 border-yellow-300">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Crown className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-900">Owner Approval Required</p>
                <p className="text-sm text-yellow-700">
                  Admin payout requests require your approval. Provider daily payouts can be approved by admins.
                  Provider weekly payouts are processed automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            Pending Requests
            {pendingRequests.length > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Review and approve or reject payout requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-3" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No pending payout requests to review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <Card
                  key={request.request_id}
                  className={`border-l-4 ${
                    request.is_admin 
                      ? 'border-l-purple-500 bg-purple-50/30' 
                      : 'border-l-blue-500 bg-blue-50/30'
                  }`}
                  data-testid={`payout-request-${request.request_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Left: User Info */}
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${request.is_admin ? 'bg-purple-100' : 'bg-blue-100'}`}>
                          {getTypeIcon(request)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{request.user_name}</span>
                            {getTypeBadge(request)}
                            {getFrequencyBadge(request.frequency)}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {request.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(request.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <ArrowUpRight className="h-3 w-3" />
                              Requires: {request.requires_approval_from === 'owner' ? 'Owner' : 'Admin'} approval
                            </span>
                          </div>
                          {request.notes && (
                            <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded">
                              Note: {request.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Right: Amount & Actions */}
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            ${request.amount?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-400">Payout amount</p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRejectDialog(request)}
                            disabled={processing === request.request_id}
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            data-testid={`reject-payout-${request.request_id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(request.request_id)}
                            disabled={processing === request.request_id}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-payout-${request.request_id}`}
                          >
                            {processing === request.request_id ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin Pending Warning - Only for Owner */}
      {isOwner && stats && stats.admin_pending_amount > 0 && (
        <Card className="border-purple-300 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-semibold text-purple-900">Admin Payouts Pending Your Approval</p>
                <p className="text-sm text-purple-700">
                  ${stats.admin_pending_amount?.toFixed(2)} in admin payout requests require your approval.
                  Only you can approve admin payouts.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              Reject Payout Request
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this payout request from {selectedRequest?.user_name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Amount</span>
                <span className="font-bold text-red-600">${selectedRequest?.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-sm text-gray-500">Type</span>
                <span className="text-sm">{selectedRequest?.description}</span>
              </div>
            </div>
            
            <Textarea
              placeholder="Enter rejection reason (e.g., 'Insufficient balance', 'Pending verification', etc.)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              data-testid="rejection-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing}
              data-testid="confirm-reject-btn"
            >
              {processing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Reject Payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayoutApprovalDashboard;
